import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, useMotionValue, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Droplets } from 'lucide-react';

const DealerLogos = ({ compact = false, showIrrigationDealers = true }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Measure container and content widths for scrolling
  useEffect(() => {
    if (containerRef.current && contentRef.current) {
      const updateWidths = () => {
        setContainerWidth(containerRef.current?.clientWidth || 0);
        setContentWidth(contentRef.current?.scrollWidth || 0);
      };
      
      updateWidths();
      window.addEventListener('resize', updateWidths);
      return () => window.removeEventListener('resize', updateWidths);
    }
  }, []);

// Set up back-and-forth scrolling animation
useEffect(() => {
  if (contentWidth > containerWidth && !isInteracting && contentRef.current) {
    const scrollDistance = contentWidth - containerWidth;

    controls.start({
      x: [0, -scrollDistance],
      transition: {
        duration: scrollDistance / 50,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse",
        repeatDelay: 0.5
      }
    });

    return () => {
      controls.stop();
    };
  }
}, [containerWidth, contentWidth, controls, isInteracting, contentRef]);

  // Irrigation dealers
  const irrigationDealers = [
    { 
      name: 'Skone Irrigation',
      url: 'https://skoneirrigation.com/',
      logo: '/dealer-logos/skoneirrigation.png',
      fallbackText: 'SKONE',
      color: '#e11b22'
    },
    { 
      name: 'Avid Water',
      url: 'https://avidwater.com/',
      logo: '/dealer-logos/avidwater.png',
      fallbackText: 'AVID WATER',
      color: '#1e9d49'
    },
    { 
      name: 'Irrigation Specialists',
      url: 'https://irrigationspecialists.com/',
      logo: '/dealer-logos/irrigationspecialists.png',
      fallbackText: 'IRRIGATION SPECIALISTS',
      color: '#e11b22'
    },
    { 
      name: 'Irrigators Inc',
      url: 'https://www.irrigatorsinc.com/',
      logo: '/dealer-logos/irrigatorsinc.png',
      fallbackText: 'IRRIGATORS INC',
      color: '#e11b22'
    },
    { 
      name: 'Elmers Irrigation',
      url: 'https://elmersirrigationinc.com/',
      logo: '/dealer-logos/elmersirrigation.png',
      fallbackText: 'ELMERS',
      color: '#2e5b41'
    },
  ];
  
  // Other dealers
  const otherDealers = [
    { 
      name: 'LAD Irrigation',
      url: 'https://www.ladirrigation.com/',
      logo: '/dealer-logos/ladirrigation.png',
      fallbackText: 'LAD',
      color: '#7cb342'
    },
    { 
      name: 'OVS',
      url: 'https://www.ovs.com/',
      logo: '/dealer-logos/ovs.png',
      fallbackText: 'OVS',
      color: '#1e40af'
    },
    { 
      name: 'RDO Equipment',
      url: 'https://www.rdoequipment.com/irrigation/rdo-water',
      logo: '/dealer-logos/rdoequipment.jpg',
      fallbackText: 'RDO',
      color: '#000000'
    },
    { 
      name: 'Foothills Irrigation',
      url: 'https://www.foothillsirrigation.com/',
      logo: '/dealer-logos/foothillsirrigation.jpg',
      fallbackText: 'FOOTHILLS',
      color: '#4f46e5'
    },
    { 
      name: 'Akland Pump & Irrigation',
      url: 'https://aklandpump.com/',
      logo: '/dealer-logos/aklandpump.png',
      fallbackText: 'AKLAND',
      color: '#dc2626'
    },
    { 
      name: 'CalWest Rain',
      url: 'https://calwestrain.com/',
      logo: '/dealer-logos/calwestrain.png',
      fallbackText: 'CALWEST',
      color: '#0891b2'
    },
    { 
      name: 'Ewing Outdoor Supply',
      url: 'https://www.ewingoutdoorsupply.com/',
      logo: '/dealer-logos/ewing.png',
      fallbackText: 'EWING',
      color: '#7cb342'
    },
    { 
      name: 'Ferguson Waterworks',
      url: 'https://www.fergusonwaterworks.com/',
      logo: '/dealer-logos/fergusonwaterworks.png',
      fallbackText: 'FERGUSON',
      color: '#1d4ed8'
    },
    { 
      name: 'HD Fowler Company',
      url: 'https://www.hdfowler.com/',
      logo: '/dealer-logos/hdfowler.png',
      fallbackText: 'HD FOWLER',
      color: '#1e3a8a'
    },
    { 
      name: 'Morton\'s Supply Yakima', 
      url: null, // No link
      logo: null, // No logo
      fallbackText: 'MORTON\'S SUPPLY',
      color: '#059669'
    }
  ];

  // Combine dealers based on page type
  const dealers = showIrrigationDealers 
    ? [...irrigationDealers, ...otherDealers]
    : otherDealers;

  const LogoComponent = ({ dealer, index }: { dealer: any, index: number }) => {
    const [imageError, setImageError] = React.useState(false);
    const [imageLoaded, setImageLoaded] = React.useState(false);
    
    // Log the logo path for debugging
    React.useEffect(() => {
      console.log(`Attempting to load logo: ${dealer.name} - ${dealer.logo}`);
    }, [dealer.name, dealer.logo]);

    const content = (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 min-w-[180px] h-20 flex items-center justify-center p-4">
        {dealer.logo && !imageError ? (
          <img
            src={dealer.logo}
            alt={`${dealer.name} logo`}
            className="max-h-16 max-w-full object-contain"
            loading="eager"
            onError={() => setImageError(true)}
            onLoad={() => setImageLoaded(true)}
          />
        ) : (
          // Fallback to styled text if no logo or image fails to load
          <span 
            className="font-bold text-lg tracking-wide text-center"
            style={{ color: dealer.color }}
          >
            {dealer.fallbackText}
          </span>
        )}
      </div>
    );

    if (dealer.url) {
      return (
        <a
          key={index}
          href={dealer.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block transition-all duration-300 flex-shrink-0 hover:scale-105 hover:-translate-y-1"
        >
          {content}
        </a>
      );
    }

    return (
      <div
        key={index}
        className="transition-all duration-300 flex-shrink-0 hover:scale-105 hover:-translate-y-1"
      >
        {content}
      </div>
    );
  };

  // Don't show slider if content fits in container
  const showSlider = contentWidth > containerWidth;

  const handleDealerMapClick = (e) => {
    e.preventDefault();
    navigate('/filtration#dealer-map');
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const element = document.getElementById('dealer-map');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className={`${compact || isMobile ? 'mb-6 py-4' : 'mb-12 py-8'} overflow-hidden`}>
      <div className="mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`text-center ${compact || isMobile ? 'mb-4' : 'mb-8'}`}
        >
          <h3 className={`${compact || isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 mb-2`}>
            {showIrrigationDealers ? 'Irrigation & Filtration Partners' : 'Authorized Dealers'}
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            {showIrrigationDealers 
              ? 'Our filtration systems are available through these trusted irrigation dealers'
              : 'Available through these authorized dealers and partners'}
          </p>
        </motion.div>
      </div>

      {/* Scrollable container */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden pb-4"
      >
        <motion.div 
          ref={contentRef}
          className="flex space-x-6 items-center"
          animate={controls}
          initial={{ x: 0 }}
        >
          {dealers.map((dealer, index) => (
            <LogoComponent key={`${dealer.name}-${index}`} dealer={dealer} index={index} />
          ))}
        </motion.div>
      </div>

      {/* Link to full dealer map - only show on home page */}
      {showIrrigationDealers && !window.location.pathname.includes('/filtration') && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-6"
        >
          <button
            onClick={handleDealerMapClick}
            className="inline-flex items-center space-x-2 text-ctc-blue hover:text-ctc-blue-dark transition-colors font-medium text-lg"
          >
            <span>Full Dealer List & Map</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </motion.div>
      )}

    </div>
  );
};

export default DealerLogos;